"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import CdnImage from "@/components/CdnImage";
import { useT } from "@/hooks/useT";

interface RelatedEvent {
  slug: string;
  title: string;
  image: string | null;
  volume24hr: number;
  endDate: string | null;
  live: boolean;
}

interface Props {
  eventSlug: string;
}

function formatVol(v: number): string {
  if (!Number.isFinite(v) || v <= 0) return "$0";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

/**
 * Renders "Related markets" — up to 8 events sharing tags with the
 * current event. Data comes from the backend `/api/markets/related-
 * events/:slug` endpoint which fans out to gamma `/events?tag_slug=…`
 * for the top 3 tags of the source event, then dedups + sorts by
 * 24h volume. Cached 5 min server-side; we don't poll.
 *
 * Renders nothing while loading or if the related list is empty.
 */
export default function RelatedEventsByTags({ eventSlug }: Props) {
  const t = useT();
  const [events, setEvents] = useState<RelatedEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/polymarket/related-events/${encodeURIComponent(eventSlug)}`)
      .then(r => r.ok ? r.json() : [])
      .then((data) => {
        if (cancelled) return;
        setEvents(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setEvents([]);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [eventSlug]);

  // Same header chrome as KalshiRelatedSeries / OpenOrders so the
  // sidebar looks uniform — boxed card, divider under the title,
  // monument font, accent-tinted icon left of the title.
  const HeaderIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 dark:text-gray-500">
      <circle cx="6" cy="6" r="3" />
      <circle cx="18" cy="18" r="3" />
      <path d="M8.5 8.5l7 7" />
    </svg>
  );

  if (loading) {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-[#1A1A1A]">
          <div className="flex items-center gap-1.5">
            {HeaderIcon}
            <h3 className="font-monument text-base md:text-lg text-gray-900 dark:text-white">
              {t("eventSidebar.relatedMarkets") || "Related markets"}
            </h3>
          </div>
        </div>
        <div className="p-3 space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (events.length === 0) return null;

  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-[#1A1A1A]">
        <div className="flex items-center gap-1.5">
          {HeaderIcon}
          <h3 className="font-monument text-base md:text-lg text-gray-900 dark:text-white">
            {t("eventSidebar.relatedMarkets") || "Related markets"}
          </h3>
        </div>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-[#1A1A1A]/50">
        {events.slice(0, 5).map((e) => (
          <Link
            key={e.slug}
            href={`/polymarket/event/${e.slug}`}
            className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#0f0f12] transition-colors group"
          >
            {e.image ? (
              <CdnImage
                src={e.image}
                alt={e.title}
                width={32}
                height={32}
                className="w-8 h-8 rounded-[5px] object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-[5px] bg-gray-200 dark:bg-[#0f0f12] flex-shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-gray-900 dark:text-white truncate group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                {e.title}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                {e.live && (
                  <span className="text-[9px] font-bold text-red-500 tracking-wider">LIVE</span>
                )}
                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                  {formatVol(e.volume24hr)} 24h
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
